import React, { useState, useEffect, useRef, useCallback } from 'react';
import avulsaService from '../services/avulsaService';
import Button from './ui/Button';
import { maskCNPJ, isValidCNPJ } from '../utils/masks'; // #287 — CNPJ deferido pro checkout
import './Login.css';

/**
 * Análise Avulsa V1 — checkout + polling (PR4c).
 *
 * Fecha o passo PAGO do funil: gera a cobrança (Pix manual), mostra a instrução
 * de pagamento e fica observando o workspace-status (durável) até a análise ficar
 * pronta. NÃO consome IA — a IA só roda no backend após `paid` (confirm-test do
 * super-admin em V1; webhook Pix quando o gateway entrar).
 *
 * Pix "Pix-ready": se o backend tiver AVULSA_PIX_* setado, mostra chave/QR/recebedor
 * reais; senão (staging/lançamento) degrada gracioso — a cobrança é criada e a
 * confirmação é manual. Polling espelha o useAnalysisStatus (setInterval + cleanup
 * no unmount + timeout), mas bate no /workspace-status com o avulsa_token.
 */

const POLL_MS = 3000;
const POLL_TIMEOUT_MS = 8 * 60 * 1000; // análise + espera de confirmação
const TERMINAL = ['completed', 'failed', 'expired'];

function AvulsaCheckout({ numeroNc, onCompleted, onReview }) {
  const [ws, setWs] = useState(null);          // último workspace-status
  const [charge, setCharge] = useState(null);  // resposta do /charge (campos pix_*)
  const [charging, setCharging] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  // CNPJ deferido do signup (#287): coletado aqui, no momento de pagar. É a razão
  // social oficial do relatório + a trava de dedup 1×/CNPJ. Enviado no /charge.
  const [cnpj, setCnpj] = useState('');
  const cnpjInvalido = cnpj.trim().length > 0 && !isValidCNPJ(cnpj);

  const startedAtRef = useRef(null);
  const intervalRef = useRef(null);
  const completedFiredRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const s = await avulsaService.workspaceStatus();
      setWs(s);
      if (s.status === 'completed' && !completedFiredRef.current) {
        completedFiredRef.current = true;
        stopPolling();
        if (onCompleted) onCompleted(s);
      } else if (TERMINAL.includes(s.status)) {
        stopPolling();
      }
      return s;
    } catch (err) {
      if (err.status === 401) {
        stopPolling();
        avulsaService.clearSession();
        window.location.assign('/avulsa'); // sessão morta → recomeça
      }
      return null; // erro transitório → mantém o polling
    }
  }, [onCompleted, stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    startedAtRef.current = Date.now();
    intervalRef.current = setInterval(() => {
      if (Date.now() - (startedAtRef.current || 0) > POLL_TIMEOUT_MS) {
        stopPolling();
        return;
      }
      fetchStatus();
    }, POLL_MS);
  }, [fetchStatus, stopPolling]);

  // monta: status imediato + liga o polling; desliga no unmount
  useEffect(() => {
    fetchStatus();
    startPolling();
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCharge = async () => {
    setError('');
    if (!cnpj.trim() || !isValidCNPJ(cnpj)) {
      setError('Informe um CNPJ válido — ele identifica a empresa no relatório e na garantia de reembolso.');
      return;
    }
    setCharging(true);
    avulsaService.event('pay_clicked');
    try {
      const res = await avulsaService.charge(avulsaService.getChargeIdempotencyKey(), cnpj);
      setCharge(res);
      fetchStatus(); // garante sync do status durável
    } catch (err) {
      setError(
        err.status === 409
          ? 'Esta cobrança já foi criada ou paga. Aguarde a confirmação.'
          : (err.message || 'Não foi possível gerar a cobrança. Tente novamente.')
      );
    } finally {
      setCharging(false);
    }
  };

  const handleRetry = async () => {
    setError('');
    setRetrying(true);
    try {
      await avulsaService.retry();
      completedFiredRef.current = false;
      startPolling();
      fetchStatus();
    } catch (err) {
      setError(err.message || 'Não foi possível tentar de novo agora.');
    } finally {
      setRetrying(false);
    }
  };

  const copy = (label, value) => {
    if (!value || !navigator.clipboard) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(''), 2000);
    }).catch(() => {});
  };

  const status = ws ? ws.status : null;
  const numero = numeroNc || (ws && ws.numero_nc) || null;
  const hasPix = !!(charge && charge.pix_key);

  // ── helpers de render ───────────────────────────────────────────────────────
  const Shell = ({ children, maxWidth = 560 }) => (
    <div className="login-page-wrapper" style={{ alignItems: 'flex-start', padding: '32px 16px', overflowY: 'auto' }}>
      <div className="login-grid-container" style={{ gridTemplateColumns: '1fr', maxWidth }}>
        <div className="login-form-column">{children}</div>
      </div>
    </div>
  );

  const NumeroLine = () => (
    numero ? (
      <p className="version-text" style={{ marginTop: 4 }}>NC <strong>{numero}</strong></p>
    ) : null
  );

  const Spinner = ({ size = 40 }) => (
    <span className="material-symbols-outlined"
      style={{ fontSize: size, color: '#10b981', animation: 'spin 1s linear infinite' }}>
      progress_activity
    </span>
  );

  // ── 0) carregando o status inicial ──────────────────────────────────────────
  if (!ws) {
    return (
      <Shell>
        <div style={{ textAlign: 'center', padding: '2rem 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <Spinner />
          <p className="form-subtitle">Carregando o checkout…</p>
        </div>
      </Shell>
    );
  }

  // ── análise pronta (transição pro resultado já disparou) ─────────────────────
  if (status === 'completed') {
    return (
      <Shell>
        <div style={{ textAlign: 'center', padding: '2rem 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 56, color: '#10b981' }}>verified</span>
          <h2 className="form-title">Sua análise está pronta!</h2>
          <NumeroLine />
        </div>
      </Shell>
    );
  }

  // ── falha técnica (retry grátis — não cobra de novo) ─────────────────────────
  if (status === 'failed') {
    return (
      <Shell>
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <span className="material-symbols-outlined" style={{ fontSize: 52, color: '#f59e0b' }}>error</span>
          <h2 className="form-title" style={{ marginTop: 8 }}>Falha técnica ao gerar a análise</h2>
          <p className="form-subtitle" style={{ maxWidth: 440, margin: '8px auto 0' }}>
            Tivemos um problema ao gerar sua investigação — <strong>você não foi cobrado por isso</strong>.
            É só tentar de novo.
          </p>
          <NumeroLine />
          {error && <div className="error-message" style={{ marginTop: 12 }}>{error}</div>}
          <div style={{ marginTop: 20 }}>
            <Button type="button" onClick={handleRetry} disabled={retrying}>
              {retrying ? 'TENTANDO…' : 'TENTAR DE NOVO'}
              <span className="material-symbols-outlined" style={{ marginLeft: '0.5rem' }}>refresh</span>
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  // ── janela expirada ──────────────────────────────────────────────────────────
  if (status === 'expired') {
    return (
      <Shell>
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <span className="material-symbols-outlined" style={{ fontSize: 52, color: '#94a3b8' }}>schedule</span>
          <h2 className="form-title" style={{ marginTop: 8 }}>A janela desta análise expirou</h2>
          <p className="form-subtitle" style={{ maxWidth: 440, margin: '8px auto 0' }}>
            Para gerar uma nova análise ou gerir a qualidade de forma contínua,
            conheça os planos da Qaa.sa.
          </p>
          <div style={{ marginTop: 16 }}>
            <a href="/" className="forgot-link">Ver planos</a>
          </div>
        </div>
      </Shell>
    );
  }

  // ── pagamento confirmado → gerando análise (paid / analyzing) ────────────────
  if (status === 'paid' || status === 'analyzing') {
    return (
      <Shell>
        <div style={{ textAlign: 'center', padding: '2rem 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <Spinner size={44} />
          <h2 className="form-title">Pagamento confirmado!</h2>
          <p className="form-subtitle" style={{ maxWidth: 440 }}>
            Estamos gerando sua investigação completa com IA — fica pronta em menos de
            5 minutos (normalmente em 1 a 2). Pode deixar esta tela aberta; ela atualiza sozinha.
          </p>
          <NumeroLine />
        </div>
      </Shell>
    );
  }

  // ── pending_payment: antes da cobrança → CTA; depois → instrução + espera ────
  return (
    <Shell maxWidth={charge ? 600 : 520}>
      <div style={{ textAlign: 'center' }}>
        <img src="/qaasa-texto-dark.png" alt="Qaa.sa" style={{ height: 26, marginBottom: 10 }} />
        <h2 className="form-title">Gerar sua análise</h2>
        <NumeroLine />
      </div>

      {!charge ? (
        // ── CTA de pagamento ─────────────────────────────────────────────────
        <>
          <div style={{
            margin: '18px 0', padding: '16px 18px', background: '#f8fafc',
            border: '1px solid #e2e8f0', borderRadius: 8,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: 15, color: '#334155' }}>Investigação completa de NC</span>
              <strong style={{ fontSize: 20, color: '#0f172a' }}>R$ 75</strong>
            </div>
            <p style={{ fontSize: 12, color: '#64748b', margin: '8px 0 0' }}>
              Pagamento único via Pix. A IA gera a causa raiz (Ishikawa 6M + 5 porquês),
              o plano de ação e um PDF — <strong>seu pra sempre</strong>. Sem assinatura.
              Não serviu? <strong>Garantia de 7 dias, sem perguntas — uma vez por empresa (CNPJ).</strong>
            </p>
          </div>

          {/* CNPJ (#287): coletado no pagamento, não no cadastro. */}
          <div className="form-group" style={{ marginBottom: 14, textAlign: 'left' }}>
            <label className="form-label">CNPJ da empresa</label>
            <div className="input-wrapper">
              <span className="material-symbols-outlined input-icon">badge</span>
              <input
                type="text" className="form-input" placeholder="00.000.000/0000-00"
                value={cnpj} onChange={(e) => setCnpj(maskCNPJ(e.target.value))}
                inputMode="numeric" disabled={charging}
              />
            </div>
            {cnpjInvalido ? (
              <p style={{ fontSize: 12, color: '#dc2626', margin: '6px 2px 0' }}>
                CNPJ inválido — confira os números.
              </p>
            ) : (
              <p style={{ fontSize: 12, color: '#94a3b8', margin: '6px 2px 0' }}>
                Vai no relatório (buscamos a razão social oficial) e garante seu direito de reembolso.
              </p>
            )}
          </div>

          {error && <div className="error-message" style={{ marginBottom: 12 }}>{error}</div>}

          <Button type="button" className="submit-btn" onClick={handleCharge}
            disabled={charging || !cnpj.trim() || cnpjInvalido}>
            {charging ? 'GERANDO COBRANÇA…' : 'PAGAR R$75 VIA PIX'}
            <span className="material-symbols-outlined" style={{ marginLeft: '0.5rem' }}>qr_code_2</span>
          </Button>

          {onReview && (
            <div style={{ textAlign: 'center', marginTop: 14 }}>
              <button className="forgot-link" style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                onClick={onReview}>
                ← Revisar os dados da NC
              </button>
            </div>
          )}
        </>
      ) : (
        // ── instrução de pagamento + espera de confirmação ───────────────────
        <>
          {hasPix ? (
            <div style={{ margin: '18px 0' }}>
              <p className="form-subtitle" style={{ textAlign: 'center', marginBottom: 14 }}>
                Pague <strong>R$75</strong> via Pix. Assim que o pagamento for confirmado,
                a análise é gerada automaticamente e fica pronta em menos de 5 minutos.
              </p>
              {charge.pix_recebedor && (
                <p style={{ fontSize: 13, color: '#475569', textAlign: 'center', margin: '0 0 12px' }}>
                  Recebedor: <strong>{charge.pix_recebedor}</strong>
                </p>
              )}
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Chave Pix</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <code style={{
                    flex: 1, padding: '10px 12px', background: '#f1f5f9', borderRadius: 6,
                    fontSize: 13, wordBreak: 'break-all',
                  }}>{charge.pix_key}</code>
                  <button type="button" className="forgot-link"
                    style={{ background: 'none', border: '1px solid #cbd5e1', borderRadius: 6, padding: '0 12px', cursor: 'pointer' }}
                    onClick={() => copy('key', charge.pix_key)}>
                    {copied === 'key' ? 'copiado!' : 'copiar'}
                  </button>
                </div>
              </div>
              {charge.pix_qr && (
                <div>
                  <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Pix copia e cola</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <code style={{
                      flex: 1, padding: '10px 12px', background: '#f1f5f9', borderRadius: 6,
                      fontSize: 12, wordBreak: 'break-all', maxHeight: 90, overflowY: 'auto',
                    }}>{charge.pix_qr}</code>
                    <button type="button" className="forgot-link"
                      style={{ background: 'none', border: '1px solid #cbd5e1', borderRadius: 6, padding: '0 12px', cursor: 'pointer' }}
                      onClick={() => copy('qr', charge.pix_qr)}>
                      {copied === 'qr' ? 'copiado!' : 'copiar'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : charge && charge.checkout_url ? (
            // fallback: o QR Pix ainda não veio na resposta (o Asaas o gera de forma
            // assíncrona). A fatura hospedada do Asaas sempre tem o QR/copia-e-cola —
            // mandamos o comprador pra lá pra que ele NUNCA fique sem como pagar. #293
            <div style={{ margin: '18px 0' }}>
              <p className="form-subtitle" style={{ textAlign: 'center', marginBottom: 14 }}>
                Pague <strong>R$75</strong> via Pix na página segura de pagamento. Assim que o
                pagamento for confirmado, a análise é gerada automaticamente e fica pronta em
                menos de 5 minutos — pode voltar para esta aba, ela atualiza sozinha.
              </p>
              <a href={charge.checkout_url} target="_blank" rel="noopener noreferrer"
                className="ui-btn ui-btn--primary ui-btn--md submit-btn"
                style={{ textDecoration: 'none' }}>
                <span>ABRIR PÁGINA DE PAGAMENTO</span>
                <span className="material-symbols-outlined ui-btn__icon" style={{ marginLeft: '0.5rem' }}>open_in_new</span>
              </a>
            </div>
          ) : (
            // degradação graciosa: Pix não configurado (staging/lançamento)
            <div style={{
              margin: '18px 0', padding: '14px 16px', background: '#fffbeb',
              border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#92400e', lineHeight: 1.6,
            }}>
              <strong>Cobrança criada.</strong> O pagamento via Pix está sendo ativado.
              Sua análise será liberada assim que confirmarmos o pagamento — você não
              precisa fazer mais nada agora; esta tela atualiza sozinha quando estiver pronta.
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 8 }}>
            <Spinner size={20} />
            <span style={{ fontSize: 13, color: '#64748b' }}>Aguardando confirmação do pagamento…</span>
          </div>

          {error && <div className="error-message" style={{ marginTop: 12 }}>{error}</div>}
        </>
      )}
    </Shell>
  );
}

export default AvulsaCheckout;
